import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { downloadsAPI, formatApiError, resolveImageUrl } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  ArrowLeft, FileArrowDown, ShoppingBag, DownloadSimple, Storefront, ArrowRight, Clock,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { profileLink } from '../utils/profile';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_moneyswap-9/artifacts/pvoha9c5_D%24.png';

const formatBytes = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch (err) { console.warn('Date format failed:', err); return ''; }
};

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

export default function MyLibraryPage() {
  const { user } = useAuth();
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);

  const loadLibrary = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await downloadsAPI.getMyLibrary();
      setPurchases(data.purchases || []);
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLibrary();
  }, [loadLibrary]);

  const handleDownload = async (purchaseId) => {
    setDownloadingId(purchaseId);
    try {
      const { data } = await downloadsAPI.getDownloadUrl(purchaseId);
      // Open the signed URL in a new tab; the ContentDisposition header forces download
      window.location.href = data.download_url;
      toast.success('Download started');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#111111]">
      <header className="bg-[#111111]/70 backdrop-blur-xl border-b border-[#333333] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Divispend" className="h-8 sm:h-10 w-auto" />
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Link to="/dashboard" className="inline-flex items-center text-sm text-[#999999] hover:text-white mb-6" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Link>

        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <FileArrowDown className="w-7 h-7 text-[#22C55E]" weight="duotone" />
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-white" style={{ fontFamily: 'Cabinet Grotesk' }}>
              My Library
            </h1>
          </div>
          <p className="text-[#999999] text-sm">
            Every digital product you've purchased on Divispend. Download anytime — your access never expires.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-16 text-[#999999]" data-testid="library-loading">Loading your library…</div>
        ) : purchases.length === 0 ? (
          <Card className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none" data-testid="library-empty">
            <CardContent className="p-10 sm:p-14 text-center">
              <ShoppingBag className="w-12 h-12 text-[#333333] mx-auto mb-4" />
              <h2 className="text-xl font-bold text-white mb-2" style={{ fontFamily: 'Cabinet Grotesk' }}>No purchases yet</h2>
              <p className="text-sm text-[#999999] mb-6 max-w-md mx-auto">
                When you buy a digital product from a Divispend seller, it'll show up here for instant download.
              </p>
              <Link to="/explore">
                <Button className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm" data-testid="library-browse-cta">
                  Browse the marketplace <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {purchases.map((p) => (
              <Card
                key={p.purchase_id}
                className="bg-[#1A1A1A] border border-[#333333] rounded-sm shadow-none overflow-hidden"
                data-testid={`library-item-${p.purchase_id}`}
              >
                <CardContent className="p-0">
                  <div className="flex flex-col sm:flex-row gap-4 p-4">
                    {/* Product image */}
                    <Link to={`/product/${p.product_id}`} className="shrink-0">
                      <div className="w-full sm:w-24 h-32 sm:h-24 bg-[#111111] rounded-sm overflow-hidden">
                        {p.product_image ? (
                          <img
                            src={resolveImageUrl(p.product_image)}
                            alt={p.product_name}
                            className="w-full h-full object-cover"
                            onError={(e) => { e.target.onerror = null; e.target.className = 'hidden'; }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <FileArrowDown className="w-10 h-10 text-[#333333]" />
                          </div>
                        )}
                      </div>
                    </Link>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 mb-1">
                        <Link
                          to={`/product/${p.product_id}`}
                          className="font-bold text-white hover:text-[#22C55E] transition-colors text-base"
                          style={{ fontFamily: 'Cabinet Grotesk' }}
                          data-testid={`library-product-link-${p.purchase_id}`}
                        >
                          {p.product_name}
                        </Link>
                        <span className="text-xs text-[#999999] shrink-0">{formatDate(p.purchased_at)}</span>
                      </div>

                      {p.seller && (
                        <Link
                          to={profileLink(p.seller)}
                          className="inline-flex items-center gap-1.5 text-xs text-[#999999] hover:text-white mb-2"
                        >
                          <Storefront className="w-3 h-3" />
                          <span>{p.seller.name}</span>
                          {p.seller.username && <span className="text-[#22C55E]">${p.seller.username}</span>}
                        </Link>
                      )}

                      <div className="flex flex-wrap items-center gap-3 text-xs text-[#999999] mb-3">
                        {p.category && (
                          <span className="bg-[#111111] px-2 py-0.5 rounded-sm">{p.category}</span>
                        )}
                        <span>Paid {formatCurrency(p.amount_paid)}</span>
                        {p.download_count > 0 && (
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {p.download_count} download{p.download_count !== 1 ? 's' : ''}
                          </span>
                        )}
                      </div>

                      {p.has_file ? (
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                          <Button
                            onClick={() => handleDownload(p.purchase_id)}
                            disabled={downloadingId === p.purchase_id}
                            className="bg-[#22C55E] hover:bg-[#16A34A] text-white rounded-sm h-10 px-5 font-semibold"
                            data-testid={`download-button-${p.purchase_id}`}
                          >
                            <DownloadSimple className="w-4 h-4 mr-2" weight="bold" />
                            {downloadingId === p.purchase_id ? 'Preparing…' : 'Download'}
                          </Button>
                          {p.filename && (
                            <span className="text-xs text-[#999999] truncate" data-testid={`download-filename-${p.purchase_id}`}>
                              {p.filename}{p.file_size ? ` · ${formatBytes(p.file_size)}` : ''}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="text-xs text-[#F59E0B] bg-[#F59E0B]/10 border border-[#F59E0B]/20 rounded-sm px-3 py-2 inline-flex items-center gap-2" data-testid={`download-pending-${p.purchase_id}`}>
                          <Clock className="w-3 h-3" />
                          The seller hasn't attached a file yet. We'll let you know when it's ready.
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
